import { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { FormInput, FormTextarea, FormSelect, FormCheckbox } from '../../../components/forms'
import { personnelService, userService, type EmployeeProfile } from '../../../services'
import { useLocale } from '../../../hooks/use-locale'
import { Save, CheckCircle } from 'lucide-react'

export default function EmployeePersonalInformationSection() {
  const { t } = useTranslation()
  const { formatDateTime } = useLocale()
  const { id } = useParams()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['employee-profile', id],
    queryFn: () => personnelService.getEmployeeProfile(id!),
    enabled: !!id,
  })

  const { data: user } = useQuery({
    queryKey: ['user', id],
    queryFn: () => userService.getUserById(id!),
    enabled: !!id,
  })

  const [form, setForm] = useState<Partial<EmployeeProfile>>({})
  const [savedAt, setSavedAt] = useState<Date | null>(null)

  const functionOptions = useMemo(
    () => [
      { value: '', label: t('common.noSelection') },
      { value: 'IT', label: t('employee.functions.it') },
      { value: 'HR', label: t('employee.functions.hr') },
      { value: 'Lager', label: t('employee.functions.warehouse') },
      { value: 'Putzkraft', label: t('employee.functions.cleaning') },
      { value: 'Pflege', label: t('employee.functions.care') },
    ],
    [t]
  )

  useEffect(() => {
    if (data) {
      setForm({
        ...data,
        firstName: data.firstName || user?.firstName || '',
        lastName: data.lastName || user?.lastName || '',
        function: data.function || user?.function || '',
        startDate:
          data.startDate ||
          (user?.startDate ? new Date(user.startDate).toISOString().split('T')[0] : ''),
        isActive: data.isActive !== undefined ? data.isActive : (user?.isActive ?? true),
      })
    } else if (user) {
      setForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        function: user.function || '',
        startDate: user.startDate ? new Date(user.startDate).toISOString().split('T')[0] : '',
        isActive: user.isActive ?? true,
      })
    }
  }, [data, user])

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked
      setForm(prev => ({ ...prev, [name]: checked }))
    } else {
      if (name === 'workHoursPerWeek') {
        setForm(prev => ({ ...prev, [name]: value === '' ? undefined : Number(value) }))
        return
      }
      if (name === 'vacationDays') {
        setForm(prev => ({
          ...prev,
          [name]: value === '' ? undefined : Number.parseInt(value, 10),
        }))
        return
      }
      setForm(prev => ({ ...prev, [name]: value }))
    }
  }

  const saveMutation = useMutation({
    mutationFn: async (payload: Partial<EmployeeProfile>) => {
      const userPayload = {
        firstName: payload.firstName,
        lastName: payload.lastName,
        function: payload.function || undefined,
        startDate: payload.startDate || undefined,
        isActive: payload.isActive,
      }
      const profilePayload = {
        salutation: payload.salutation,
        title: payload.title,
        notes: payload.notes,
        workHoursPerWeek: payload.workHoursPerWeek,
        vacationDays: payload.vacationDays,
        shoeSize: payload.shoeSize,
        clothingSize: payload.clothingSize,
      }
      await Promise.all([
        userService.updateUser(id!, userPayload as any),
        personnelService.upsertEmployeeProfile(id!, profilePayload),
      ])
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-profile', id] })
      queryClient.invalidateQueries({ queryKey: ['user', id] })
      setSavedAt(new Date())
    },
  })

  const onSave = () => {
    if (!id) return
    saveMutation.mutate({
      firstName: form.firstName,
      lastName: form.lastName,
      function: form.function,
      startDate: form.startDate,
      isActive: form.isActive,
      salutation: form.salutation,
      title: form.title,
      notes: form.notes,
      workHoursPerWeek: form.workHoursPerWeek,
      vacationDays: form.vacationDays,
      shoeSize: form.shoeSize,
      clothingSize: form.clothingSize,
    })
  }

  return (
    <Card>
      <CardHeader className='flex-row items-center justify-between'>
        <div>
          <CardTitle>{t('common.personalData')}</CardTitle>
          <p className='text-sm text-neutral-500 mt-1'>
            {savedAt && (
              <span className='inline-flex items-center gap-1 text-success-600'>
                <CheckCircle className='w-3.5 h-3.5' />
                {t('common.savedAt', { date: formatDateTime(savedAt) })}
              </span>
            )}
          </p>
        </div>
        <Button onClick={onSave} variant='primary' loading={saveMutation.isPending} disabled={isLoading}>
          <Save className='w-4 h-4 mr-1.5' />
          {t('common.save')}
        </Button>
      </CardHeader>
      <CardContent>
        <div className='space-y-8'>
          <div>
            <h3 className='text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-4'>
              {t('common.basicInformation')}
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-5'>
              <FormInput
                label={t('common.firstName')}
                name='firstName'
                value={form.firstName || ''}
                onChange={onChange}
                placeholder={t('common.firstName')}
              />
              <FormInput
                label={t('common.lastName')}
                name='lastName'
                value={form.lastName || ''}
                onChange={onChange}
                placeholder={t('common.lastName')}
              />
              <FormSelect
                label={t('common.function')}
                name='function'
                value={form.function || ''}
                onChange={onChange}
                options={functionOptions}
              />
              <FormInput
                label={t('common.startDate')}
                name='startDate'
                type='date'
                value={form.startDate || ''}
                onChange={onChange}
              />
              <div className='md:col-span-2'>
                <FormCheckbox
                  label={t('employee.activeCheckbox')}
                  name='isActive'
                  checked={form.isActive ?? true}
                  onChange={onChange}
                />
              </div>
            </div>
          </div>

          <div className='border-t border-neutral-100 pt-8'>
            <h3 className='text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-4'>
              {t('employee.personalInfoTitle')}
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-5'>
              <FormInput
                label={t('common.salutation')}
                name='salutation'
                value={typeof form.salutation === 'string' ? form.salutation : ''}
                onChange={onChange}
                placeholder={t('users.salutationPlaceholder')}
              />
              <FormInput
                label={t('common.titleField')}
                name='title'
                value={typeof form.title === 'string' ? form.title : ''}
                onChange={onChange}
                placeholder={t('users.titlePlaceholder')}
              />
              <FormInput
                label={t('employee.workHoursPerWeek')}
                name='workHoursPerWeek'
                type='number'
                value={form.workHoursPerWeek ? form.workHoursPerWeek.toString() : ''}
                onChange={onChange}
                placeholder='40'
              />
              <FormInput
                label={t('employee.vacationDaysPerYear')}
                name='vacationDays'
                type='number'
                value={form.vacationDays ? form.vacationDays.toString() : ''}
                onChange={onChange}
                placeholder='30'
              />
              <FormInput
                label={t('common.shoeSize')}
                name='shoeSize'
                value={form.shoeSize || ''}
                onChange={onChange}
                placeholder={t('users.shoeSizePlaceholder')}
              />
              <FormInput
                label={t('common.clothingSize')}
                name='clothingSize'
                value={form.clothingSize || ''}
                onChange={onChange}
                placeholder={t('users.clothingSizePlaceholder')}
              />
            </div>
            <div className='mt-5'>
              <FormTextarea
                label={t('common.notes')}
                name='notes'
                value={form.notes || ''}
                onChange={onChange}
                placeholder={t('users.notesPlaceholder')}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
